/**
 * Counts filter fields that differ from their default values.
 * Handles three special cases:
 *   - DateRange objects ({ from?, to? }): counts as 1 if either bound is set
 *   - NumFilter objects ({ value: string, op: string }): counts if .value !== default's .value
 *   - Everything else: strict equality check
 */
export function countNonDefault<T extends object>(
  filters: T,
  defaults: T,
  exclude: (keyof T)[] = [],
): number {
  const excludeSet = new Set(exclude);
  return (Object.keys(defaults) as (keyof T)[]).reduce((count, key) => {
    if (excludeSet.has(key)) return count;
    const current = filters[key];
    const def = defaults[key];
    // DateRange: { from?: Date; to?: Date }
    if (
      current !== null &&
      typeof current === 'object' &&
      ('from' in (current as object) || 'to' in (current as object))
    ) {
      const r = current as { from?: unknown; to?: unknown };
      return r.from || r.to ? count + 1 : count;
    }
    // NumFilter: { op: string; value: string } — compare only .value
    if (
      current !== null &&
      typeof current === 'object' &&
      'value' in (current as object) &&
      def !== null &&
      typeof def === 'object' &&
      'value' in (def as object)
    ) {
      return (current as unknown as { value: unknown }).value !==
        (def as unknown as { value: unknown }).value
        ? count + 1
        : count;
    }
    return current !== def ? count + 1 : count;
  }, 0);
}
